import React, { useEffect, useRef, useState } from 'react'
import GooglePlacesAutocomplete, { geocodeByPlaceId, getLatLng } from 'react-google-places-autocomplete'
import { withFloatingSurface } from 'components/Common/selectSurface'

export function ClubAddressPicker({ country, value, onChange, onBusy, locked, error }) {
  const [failure, setFailure] = useState('')
  const [loading, setLoading] = useState(false)
  const [retry, setRetry] = useState(0)
  const [selected, setSelected] = useState(null)
  const generation = useRef(0)
  useEffect(() => {
    generation.current += 1
    setFailure(''); setSelected(null); setLoading(false); onBusy(false)
    return () => { generation.current += 1 }
  }, [country?.key, onBusy])
  const choose = async option => {
    const request = ++generation.current
    setSelected(option); setFailure(''); onChange(null)
    if (!option) { setLoading(false); onBusy(false); return }
    setLoading(true); onBusy(true)
    try {
      const [result] = await geocodeByPlaceId(option.value.place_id)
      const coords = await getLatLng(result)
      const part = type => result?.address_components?.find(item => item.types?.includes(type))
      if (part('country')?.short_name?.toLowerCase() !== country.key.toLowerCase()) throw new Error('Choose an address in the selected country.')
      const city = part('locality')?.long_name || part('postal_town')?.long_name || part('administrative_area_level_2')?.long_name
      const physical = result?.types?.some(type => ['street_address', 'premise', 'subpremise', 'establishment', 'point_of_interest', 'park', 'route'].includes(type))
      if (!physical || !city || !Number.isFinite(coords?.lat) || !Number.isFinite(coords?.lng)) throw new Error('Choose the physical club address, including its street or venue.')
      if (request === generation.current) onChange({ placeId: option.value.place_id, country: country.name,
        city, name: result.formatted_address, coords })
    } catch (failure) {
      if (request === generation.current) setFailure(failure.message || 'Could not verify this address. Try again.')
    } finally {
      if (request === generation.current) { setLoading(false); onBusy(false) }
    }
  }
  return <div className="form-group mb-0">
    <label htmlFor="club-location">Club address<span aria-hidden="true"> *</span></label>
    {country ? <GooglePlacesAutocomplete key={country.key + ':' + retry} apiKey={process.env.REACT_APP_GOOGLE_MAPS_API}
      apiOptions={{ language: 'en' }} minLengthAutocomplete={2}
      onLoadFailed={() => setFailure('Could not load address search. Try again.')}
      autocompletionRequest={{ componentRestrictions: { country: country.key.toLowerCase() } }}
      selectProps={{ styles: withFloatingSurface(),  classNamePrefix: 'club-select', inputId: 'club-location', 'aria-label': 'Club address', 'aria-required': true,
        'aria-invalid': Boolean(error || failure), 'aria-describedby': 'club-location-help',
        value: value ? { label: value.name, value } : selected,
        onChange: choose, isClearable: true, isDisabled: locked, isLoading: loading,
        placeholder: 'Search for the club or street address…', noOptionsMessage: () => 'Type the club or street address',
      }} /> : <input className="form-control" id="club-location" disabled placeholder="Select a country first" />}
    <small id="club-location-help" className={failure || error ? 'text-danger' : 'text-muted'}>
      {loading ? 'Verifying address…' : failure || error || 'Select the physical club address from the search results.'}
    </small>
    {failure && <button type="button" className="btn btn-link p-0" disabled={locked} onClick={() => selected ? choose(selected) : setRetry(value => value + 1)}>Retry address search</button>}
  </div>
}
